/**
 * ProfileKeysLibrary (/crm/settings/profile-keys) — the Field-tag library:
 * one curated list of the cross-study tags (crm.profile_keys, the "code
 * names" attached to questions) so builders pick from a shared vocabulary
 * instead of inventing spellings per form.
 *
 * What it does:
 *  - lists every tag with label/type/unit/status + WHERE it's used at
 *    authoring time (form questions tagged medical_fact, active prescreen
 *    criteria) via GET /profile-keys?usage=true;
 *  - surfaces UNREGISTERED tags (free-text keys typed before the library
 *    existed) for one-click registration;
 *  - curates drafts minted by prescreen publishes (activate / relabel /
 *    archive — admin-only, mirroring the PATCH gate);
 *  - jumps straight into a patient-database search prefilled with the tag
 *    (condition.* → condition filter, med.* → medication filter, numeric
 *    tags → lab filter by label).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Archive, Loader2, Pencil, Plus, Search, Tags } from 'lucide-react';
import api from '../../../services/api';
import PageHeader, { RefreshButton } from '../../components/PageHeader';
import Modal from '../../components/forms/Modal';
import { bustProfileKeysCache, PROFILE_KEY_RE } from '../../components/forms/ProfileKeyPicker';
import { FilterBar, SearchInput } from '../../components/filters';
import { chipCls, fieldCls, primaryBtnCls, secondaryBtnCls } from '../../components/filters/styles';
import { useIsCRMAdmin } from '../../lib';
import { Alert, Badge, type BadgeTone } from '../../components/ui';
import type { ProfileKey, ProfileKeyUsage, UnregisteredProfileKey } from '../../types';

const VALUE_TYPES = [
  { value: 'boolean', label: 'Yes / no' },
  { value: 'number', label: 'Number' },
  { value: 'choice', label: 'Choice' },
  { value: 'text', label: 'Text' },
  { value: 'date', label: 'Date' },
];
const typeLabel = (t: string) => VALUE_TYPES.find((v) => v.value === t)?.label || t;

const STATUS_TONE: Record<string, BadgeTone> = {
  active: 'green',
  draft: 'amber',
  archived: 'gray',
};

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

/**
 * Deep link into the Search builder pre-seeded with a one-step funnel for this
 * tag (the board's medical filters moved to the Search tab), or null.
 */
function searchTarget(key: string, valueType: string, label: string): { to: string; hint: string } | null {
  const seed = (condition: Record<string, unknown>) =>
    `/crm/search/new?seed=${encodeURIComponent(JSON.stringify({
      version: 1,
      base: { exclude_archived: true, exclude_test: true },
      steps: [{ id: 's_tag', mode: 'include', match: 'any', conditions: [condition] }],
    }))}`;
  if (key.startsWith('condition.')) {
    return { to: seed({ kind: 'condition', tag: key, statuses: ['active'] }), hint: 'Search patients with this condition' };
  }
  if (key.startsWith('med.') || key.startsWith('medication.')) {
    return { to: seed({ kind: 'medication', tag: key, statuses: ['active'] }), hint: 'Search patients on this medication' };
  }
  if (valueType === 'number') {
    return { to: seed({ kind: 'lab', tag: key, tag_label: label || undefined }), hint: 'Search patients by this lab/vital' };
  }
  return null;
}

/** "2 forms · 1 study" summary with the titles in the hover tooltip. */
function UsageCell({ usage }: { usage: ProfileKeyUsage | undefined }) {
  const forms = usage?.forms || [];
  const studies = usage?.studies || [];
  if (forms.length === 0 && studies.length === 0) {
    return <span className="text-gray-400 dark:text-gray-500">—</span>;
  }
  const parts: string[] = [];
  if (forms.length > 0) parts.push(`${forms.length} form${forms.length === 1 ? '' : 's'}`);
  if (studies.length > 0) parts.push(`${studies.length} ${studies.length === 1 ? 'study' : 'studies'}`);
  const tooltip = [
    ...forms.map((f) => `Form: ${f.title}`),
    ...studies.map((s) => `Prescreen: ${s.title}`),
  ].join('\n');
  return (
    <span title={tooltip} className="cursor-help underline decoration-dotted decoration-gray-300 underline-offset-2 dark:decoration-gray-600">
      {parts.join(' · ')}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Create / edit modals
// ---------------------------------------------------------------------------

function CreateTagModal({
  seedKey,
  onClose,
  onSaved,
}: {
  seedKey: string;
  onClose: () => void;
  onSaved: (tag: ProfileKey, existed: boolean) => void;
}) {
  const [key, setKey] = useState(seedKey);
  const [label, setLabel] = useState('');
  const [valueType, setValueType] = useState('text');
  const [unit, setUnit] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const normalizedKey = key.trim().toLowerCase();
  const keyValid = PROFILE_KEY_RE.test(normalizedKey) && normalizedKey.length <= 120;

  const save = async () => {
    if (busy) return;
    if (!keyValid) { setError('The key must be dotted lowercase — e.g. condition.t2dm or med.glp1.'); return; }
    if (!label.trim()) { setError('Give the tag a human label (shown on profiles and in searches).'); return; }
    setBusy(true);
    setError(null);
    try {
      const res = await api.post('/api/crm/profile-keys', {
        key: normalizedKey,
        label: label.trim(),
        value_type: valueType,
        unit: unit.trim() || undefined,
        description: description.trim() || undefined,
      });
      onSaved(res.data.profile_key as ProfileKey, !!res.data.existed);
    } catch (e) {
      setError(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Register a field tag" maxWidth="max-w-lg">
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Key</label>
          <input
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="e.g. condition.t2dm"
            className={`${fieldCls} w-full font-mono`}
            autoFocus
          />
          <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
            Dotted lowercase, permanent once used. Prefixes drive search: <span className="font-mono">condition.*</span>,{' '}
            <span className="font-mono">med.*</span>, or a plain key for labs/vitals (e.g. <span className="font-mono">bmi</span>).
          </p>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Label</label>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Type 2 diabetes"
            className={`${fieldCls} w-full`}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Answer type</label>
            <select value={valueType} onChange={(e) => setValueType(e.target.value)} className={`${fieldCls} w-full cursor-pointer`}>
              {VALUE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Unit (optional)</label>
            <input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="e.g. kg/m²" className={`${fieldCls} w-full`} />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Description (optional)</label>
          <textarea
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What this tag means / when to use it"
            className={`${fieldCls} w-full`}
          />
        </div>
        {error && (
          <Alert tone="error" icon={false}>{error}</Alert>
        )}
        <div className="flex justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
          <button type="button" onClick={onClose} className={secondaryBtnCls}>Cancel</button>
          <button type="button" onClick={save} disabled={busy} className={primaryBtnCls}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Register tag
          </button>
        </div>
      </div>
    </Modal>
  );
}

function EditTagModal({
  tag,
  onClose,
  onSaved,
}: {
  tag: ProfileKey;
  onClose: () => void;
  onSaved: (tag: ProfileKey) => void;
}) {
  const [label, setLabel] = useState(tag.label);
  const [valueType, setValueType] = useState(tag.value_type);
  const [unit, setUnit] = useState(tag.unit || '');
  const [description, setDescription] = useState(tag.description || '');
  const [status, setStatus] = useState(tag.status);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const used = (tag.usage?.forms.length || 0) + (tag.usage?.studies.length || 0) > 0;

  const save = async () => {
    if (busy) return;
    if (!label.trim()) { setError('The label is required.'); return; }
    setBusy(true);
    setError(null);
    try {
      const res = await api.patch(`/api/crm/profile-keys/${tag.id}`, {
        label: label.trim(),
        value_type: valueType,
        unit: unit.trim(),
        description: description.trim(),
        status,
      });
      onSaved({ ...tag, ...(res.data.profile_key as ProfileKey) });
    } catch (e) {
      setError(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Edit field tag" maxWidth="max-w-lg">
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Key</label>
          <p className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 font-mono text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300">
            {tag.key}
          </p>
          <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
            Keys are permanent — recorded answers reference them. Archive the tag instead of renaming.
          </p>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Label</label>
          <input value={label} onChange={(e) => setLabel(e.target.value)} className={`${fieldCls} w-full`} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Answer type</label>
            <select value={valueType} onChange={(e) => setValueType(e.target.value)} className={`${fieldCls} w-full cursor-pointer`}>
              {VALUE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            {used && valueType !== tag.value_type && (
              <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                This tag is already in use — changing its type affects how new answers are matched.
              </p>
            )}
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Unit (optional)</label>
            <input value={unit} onChange={(e) => setUnit(e.target.value)} className={`${fieldCls} w-full`} />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Description (optional)</label>
          <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} className={`${fieldCls} w-full`} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value as ProfileKey['status'])} className={`${fieldCls} w-full cursor-pointer`}>
            <option value="active">Active — offered in pickers, feeds cross-study matching</option>
            <option value="draft">Draft — needs review (auto-minted by a prescreen publish)</option>
            <option value="archived">Archived — hidden from pickers; history kept</option>
          </select>
        </div>
        {error && (
          <Alert tone="error" icon={false}>{error}</Alert>
        )}
        <div className="flex justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
          <button type="button" onClick={onClose} className={secondaryBtnCls}>Cancel</button>
          <button type="button" onClick={save} disabled={busy} className={primaryBtnCls}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Save changes
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

// '' is the multi-select "All" chip (no status selected).
type StatusFilter = '' | 'active' | 'draft' | 'archived';

export default function ProfileKeysLibrary() {
  const navigate = useNavigate();
  const isAdmin = useIsCRMAdmin();

  const [keys, setKeys] = useState<ProfileKey[]>([]);
  const [unregistered, setUnregistered] = useState<UnregisteredProfileKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilters, setStatusFilters] = useState<string[]>([]); // empty = all
  const [editing, setEditing] = useState<ProfileKey | null>(null);
  const [createSeed, setCreateSeed] = useState<string | null>(null); // '' = blank new tag
  const [activatingId, setActivatingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/crm/profile-keys', { params: { usage: 'true' } });
      setKeys(res.data.profile_keys || []);
      setUnregistered(res.data.unregistered || []);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { all: keys.length, active: 0, draft: 0, archived: 0 };
    for (const k of keys) c[k.status] += 1;
    return c;
  }, [keys]);

  const term = search.trim().toLowerCase();
  const visible = useMemo(
    () =>
      keys.filter((k) => {
        if (statusFilters.length && !statusFilters.includes(k.status)) return false;
        if (!term) return true;
        return (
          k.key.toLowerCase().includes(term) ||
          k.label.toLowerCase().includes(term) ||
          (k.description || '').toLowerCase().includes(term)
        );
      }),
    [keys, statusFilters, term],
  );

  /** Merge a created/updated tag into the list and keep the pickers fresh. */
  const upsertLocal = (tag: ProfileKey) => {
    setKeys((prev) => {
      const rest = prev.filter((k) => k.id !== tag.id && k.key !== tag.key);
      return [...rest, { ...tag, usage: prev.find((k) => k.key === tag.key)?.usage || tag.usage }].sort((a, b) =>
        a.key.localeCompare(b.key),
      );
    });
    setUnregistered((prev) => prev.filter((u) => u.key !== tag.key));
    bustProfileKeysCache();
  };

  const activate = async (tag: ProfileKey) => {
    if (activatingId) return;
    setActivatingId(tag.id);
    try {
      const res = await api.patch(`/api/crm/profile-keys/${tag.id}`, { status: 'active' });
      upsertLocal({ ...tag, ...(res.data.profile_key as ProfileKey) });
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setActivatingId(null);
    }
  };

  const statusChips: { value: StatusFilter; label: string }[] = [
    { value: '', label: `All (${counts.all})` },
    { value: 'active', label: `Active (${counts.active})` },
    { value: 'draft', label: `Draft (${counts.draft})` },
    { value: 'archived', label: `Archived (${counts.archived})` },
  ];
  const statusActive = (v: StatusFilter) => (v === '' ? statusFilters.length === 0 : statusFilters.includes(v));
  const toggleStatus = (v: StatusFilter) => {
    if (v === '') return setStatusFilters([]); // "All" clears
    setStatusFilters(statusFilters.includes(v) ? statusFilters.filter((s) => s !== v) : [...statusFilters, v]);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        icon={Tags}
        title="Field tags"
        count={loading ? null : counts.all}
        subtitle="The shared library of cross-study tags (profile keys) — the code names attached to questions so answers are searchable across every form, questionnaire, and prescreen."
        actions={
          <>
            <RefreshButton onClick={load} loading={loading} />
            <button type="button" onClick={() => setCreateSeed('')} className={primaryBtnCls}>
              <Plus className="h-4 w-4" />
              New tag
            </button>
          </>
        }
      />

      <FilterBar>
        <SearchInput value={search} onChange={setSearch} placeholder="Search key, label, or description…" className="flex-1 basis-56 sm:max-w-xs" />
        {statusChips.map((c) => (
          <button
            key={c.value}
            type="button"
            onClick={() => toggleStatus(c.value)}
            className={chipCls(statusActive(c.value))}
            aria-pressed={statusActive(c.value)}
          >
            {c.label}
          </button>
        ))}
      </FilterBar>

      {error && (
        <Alert tone="error" icon={false}>{error}</Alert>
      )}

      {/* Free-text tags found on forms/prescreens that were never registered. */}
      {unregistered.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800/60 dark:bg-amber-900/10">
          <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
            {unregistered.length} tag{unregistered.length === 1 ? '' : 's'} in use but not in the library
          </p>
          <p className="mt-0.5 text-xs text-amber-700/80 dark:text-amber-400/80">
            These keys are typed on questions but were never registered — register them so they show up in pickers and searches.
          </p>
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {unregistered.map((u) => (
              <button
                key={u.key}
                type="button"
                onClick={() => setCreateSeed(u.key)}
                title={`Used by ${u.forms.length} form(s), ${u.studies.length} prescreen(s) — click to register`}
                className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-white px-2.5 py-1 font-mono text-xs text-amber-900 transition-colors duration-150 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-amber-700 dark:bg-gray-800 dark:text-amber-300 dark:hover:bg-amber-900/30"
              >
                <Plus className="h-3 w-3" />
                {u.key}
              </button>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
        </div>
      ) : visible.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center dark:border-gray-600">
          <Tags className="mx-auto mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" />
          <p className="mb-1 text-lg font-medium text-gray-900 dark:text-gray-100">
            {term || statusFilters.length > 0 ? 'No tags match' : 'No field tags yet'}
          </p>
          <p className="mx-auto max-w-sm text-sm text-gray-500 dark:text-gray-400">
            {term || statusFilters.length > 0
              ? 'Try a different search or status.'
              : 'Register a tag here, or create one inline while tagging a question in the form builder — publishing an AI prescreen also adds its suggested tags as drafts.'}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Tag</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Type</th>
                  <th className="hidden px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 md:table-cell">Used in</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {visible.map((k) => {
                  const target = searchTarget(k.key, k.value_type, k.label);
                  return (
                    <tr key={k.id} className="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="max-w-[22rem] px-4 py-2.5">
                        <span className="block truncate font-mono text-sm text-gray-900 dark:text-gray-100">{k.key}</span>
                        <span className="block truncate text-xs text-gray-500 dark:text-gray-400" title={k.description || undefined}>
                          {k.label}
                          {k.description ? ` — ${k.description}` : ''}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300">
                        {typeLabel(k.value_type)}
                        {k.unit ? <span className="text-gray-400 dark:text-gray-500"> · {k.unit}</span> : null}
                      </td>
                      <td className="hidden whitespace-nowrap px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 md:table-cell">
                        <UsageCell usage={k.usage} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5">
                        <Badge tone={STATUS_TONE[k.status]}>{k.status}</Badge>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-right">
                        <div className="inline-flex items-center gap-1">
                          {isAdmin && k.status === 'draft' && (
                            <button
                              type="button"
                              onClick={() => activate(k)}
                              disabled={activatingId === k.id}
                              className="rounded-lg px-2 py-1 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 dark:text-primary-400 dark:hover:bg-primary-900/20"
                            >
                              {activatingId === k.id ? 'Activating…' : 'Activate'}
                            </button>
                          )}
                          {target && (
                            <button
                              type="button"
                              onClick={() => navigate(target.to)}
                              title={target.hint}
                              aria-label={target.hint}
                              className="rounded-lg p-1.5 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700 dark:hover:text-primary-400"
                            >
                              <Search className="h-4 w-4" />
                            </button>
                          )}
                          {isAdmin && (
                            <button
                              type="button"
                              onClick={() => setEditing(k)}
                              title="Edit tag"
                              aria-label={`Edit ${k.key}`}
                              className="rounded-lg p-1.5 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                            >
                              {k.status === 'archived' ? <Archive className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-xs text-gray-400 dark:text-gray-500">
        Tags are picked (or created) right in the form and prescreen builders; publishing an AI-generated prescreen adds
        its unknown tag suggestions here as drafts for review. Active tags feed cross-study matching and the
        patient-database Condition / Medication / Lab filters.
      </p>

      {createSeed !== null && (
        <CreateTagModal
          seedKey={createSeed}
          onClose={() => setCreateSeed(null)}
          onSaved={(tag) => { upsertLocal(tag); setCreateSeed(null); }}
        />
      )}
      {editing && (
        <EditTagModal
          tag={editing}
          onClose={() => setEditing(null)}
          onSaved={(tag) => { upsertLocal(tag); setEditing(null); }}
        />
      )}
    </div>
  );
}
